import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Plus, Trash2 } from 'lucide-react';
import Header from '../../components/Header';
import Title from '../../components/Title';
import Text from '../../components/Text';
import Line from '../../components/Line';
import List from '../../components/List';
import Actions from '../../components/Actions';
import Tabs from '../../components/Tabs';
import { KEY_PARTNERS_MAX_COUNT } from '../../utils/const';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Textarea from '../../components/Textarea';
import Card from '../keyActivity/Card';
import ConfirmationModal from '../../components/ConfirmationModal';
import SvgIcon from '../../components/SvgIcon';
import TabsCut from '../../components/TabsCut';
import TabsList from '../../components/TabsList';
import TabsListItem from '../../components/TabsListItem';
import MobileMenuHeader from '../../components/MobileMenuHeader';

const icons = ['puzzles', 'people'];
const iconsComponents = icons.map((e) => <SvgIcon name={e} />);

export default function YourPartners(props) {
  const {
    nextClickHandler,
    menuItems,
    selectComponent,
    componentIndex,
    rules,
    backPath,
    locales,
    locales: {
      caseStudies,
      common,
      tipCustomerSegment,
      keyPartners: {
        name,
        yourPartners: { header, step, title, text, list, tabs },
      },
      keyActivity: {
        intro: { card },
      },
    },
  } = props;
  const {
    keyPartner: { collection },
    keyActivity,
  } = useSelector((state) => state);
  const {
    keyPartner: { addKeyPartner, updateKeyPartner, removeKeyPartner },
  } = useDispatch();
  const [showKeyActivities, setShowKeyActivities] = useState(false);
  const [opened, setOpened] = useState(false);

  return (
    <section className="bmc-page" aria-label={header}>
      <Header
        title={header}
        subtitle={name}
        icon="icon-key-partners"
        locales={locales}
        componentIndex={componentIndex}
        className="bmc-page-header--orange"
        tipLocales={tipCustomerSegment}
        menuItems={menuItems}
        onMenuItemClick={selectComponent}
        rules={rules}
        backPath={backPath}
        mobileMenuHeader={
          <MobileMenuHeader icon="KeyThingCanvas" title={name} color="orange" />
        }
      />

      <Text align="center">{[text[0]]}</Text>

      <Title text={title[0]} align="left" />

      <List items={list} icons={icons} />

      <Line />

      <h2 className="bmc-page-title">
        <span className="bmc-page-title--orange">{title[1]}</span> {title[2]}
      </h2>

      <div className="bmc-page-text bmc-page-text--center">
        <p>
          {text[1]}
          <span className="bmc-page-text--bold bmc-page-text--orange">
            {' '}
            {text[2]}
          </span>
          {text[3]}
        </p>
      </div>

      <Text align="center">{[text[4]]}</Text>

      <div className="bmc-page-links">
        <ul className="bmc-page-links-container">
          <li
            className="bmc-page-links-container-link bmc-page-links-container-link--orange"
            onClick={() => setShowKeyActivities(true)}
          >
            <span>
              {keyActivity.collection
                .map((_, index) => `${step} ${index + 1}`)
                .join('->')}
            </span>
          </li>
        </ul>
      </div>

      <Modal
        onClose={() => {
          setShowKeyActivities(false);
        }}
        isOpen={showKeyActivities}
        modalPanelClassNames="dialog-panel-large"
        headerDivider={false}
      >
        <Card
          card={{ ...card, steps: keyActivity.collection, title: null }}
          className="bmc-page-keycard--section bmc-page-keycard--section-modal"
          padding="0"
          background="white"
          boxShadow="none"
          wBlur={false}
        />
      </Modal>

      <Tabs
        tabs={[common.yourStrategy, common.caseStudy]}
        withBlur
        className="margin-top-60 bmc-page-tabs--orange"
      >
        {[
          <TabsList key="strategy">
            <TabsListItem
              divider={false}
              title={tabs.keyPartners}
              subtitle={tabs.keyPartnersSub}
              icon={iconsComponents[0]}
            >
              <div className="bmc-page-tabs-content-list-item-texts">
                {collection.map((item, index) => (
                  <div
                    key={item.id}
                    className="margin-bottom-5 flex flex-row items-center"
                  >
                    <Textarea
                      delay={700}
                      value={item.description}
                      onChange={(event) =>
                        updateKeyPartner({
                          index,
                          name: 'description',
                          value: event.target.value,
                          id: item.id,
                        })
                      }
                      placeholder={tabs.keyPartnersTextareaPlaceholder}
                    />

                    {collection.length > 1 && (
                      <ConfirmationModal
                        className="ml-3 hover:bg-muted-lighter/60 text-muted"
                        body={locales.common.deleteConfirmation}
                        confirmText="Delete"
                        isBlockScroll
                        onConfirm={() =>
                          removeKeyPartner({ index, id: item.id })
                        }
                        buttonAriaLabel="Delete"
                      >
                        <Trash2 className="w-6 min-w-6 h-auto" />
                      </ConfirmationModal>
                    )}
                  </div>
                ))}

                {collection.length < KEY_PARTNERS_MAX_COUNT && (
                  <div className="bmc-page-tabs-content-actions">
                    <Button
                      variant="outline"
                      onClick={addKeyPartner}
                      className="bg-transparent hover:bg-secondary/50 border-secondary/50 text-secondary hover:text-secondary hover:border-secondary w-full"
                    >
                      <Plus className="w-7 h-auto" />
                      {tabs.addKeyPartners}
                    </Button>
                  </div>
                )}
              </div>
            </TabsListItem>
          </TabsList>,

          <Tabs
            key="tabs"
            tabs={caseStudies.map((e) => e.name)}
            className="bmc-page-tabs--black bmc-page-tabs--inner"
          >
            {caseStudies.map((item, index) => (
              <div key={index}>
                <TabsCut
                  opened={opened}
                  setOpened={setOpened}
                  caseStudy={item}
                />

                {item.keyPartners.map((keyPartner, i) => (
                  <TabsList key={i} formStyle preview>
                    <TabsListItem
                      divider={i < item.keyPartners.length - 1}
                      title={keyPartner.name}
                      text={keyPartner.description}
                      icon={iconsComponents[0]}
                    />
                  </TabsList>
                ))}
              </div>
            ))}
          </Tabs>,
        ]}
      </Tabs>

      <Actions nextClickHandler={nextClickHandler} buttonName={common.next} />
    </section>
  );
}
